import { Injectable } from '@angular/core';
import { HttpContext, HttpResponse } from '@angular/common/http';
import { ApiService } from '@core/services/api.service';
import { DevicePvForecast, PvPerformanceResponse } from '@core/models';
import { TelemetryGranularity, TelemetryPeriod } from '@core/constants/telemetry-route.constant';
import {
  SKIP_GLOBAL_ERROR_SNACKBAR,
  SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS,
} from '@core/interceptors/http-error.interceptor';
import { map, Observable } from 'rxjs';

// Mirrors the backend's ExportGranularity enum (models/export_request.py). Omitted from the
// request body entirely when not passed, so the V1 single-device export dialog's payload is
// byte-for-byte unchanged — the backend defaults to 'quarter_hour' in that case.
export type ExportGranularity = 'quarter_hour' | 'hour' | 'day' | 'month' | 'year';

// The device energyflow route names each window by its slot, as the installation route does, and
// takes the period in the same form, so only the path segment differs.
const ENERGYFLOW_SEGMENT: Record<TelemetryGranularity, string> = {
  'quarter-hour': 'day-per-quarter-hour',
  hour: 'day-per-hour',
  week: 'week-per-day',
  day: 'month-per-day',
  month: 'year-per-month',
};

export const EXPORT_GRANULARITIES: { value: ExportGranularity; labelKey: string }[] = [
  { value: 'quarter_hour', labelKey: 'dataExport.granularity.quarterHour' },
  { value: 'hour', labelKey: 'dataExport.granularity.hour' },
  { value: 'day', labelKey: 'dataExport.granularity.day' },
  { value: 'month', labelKey: 'dataExport.granularity.month' },
  { value: 'year', labelKey: 'dataExport.granularity.year' },
];

// No 'expired' status: once a job's blobs are removed by the storage account's 180-day
// lifecycle policy, the job simply stops appearing in the history list.
export type ExportJobStatus = 'queued' | 'running' | 'completed' | 'failed';

// Wire format mirrors the backend's Pydantic models (models/export_job.py) byte-for-byte -
// this backend has no camelCase alias layer, unlike some of the other services.
export type EnergyPreset = 'ALL' | 'FORWARD' | 'BACKWARD' | 'FORWARD_BACKWARD';

/** Mirrors the backend's ExportField model (context_type + value_name are both required per its
 *  422 validation response). context_type groups the value (e.g. "heatpump"); value_name is the
 *  snake_case channel field name, matching the energy preset's 'forward_energy'/'reverse_energy'
 *  convention. Confirm exact value_name strings with the backend once its enum is finalized. */
export interface ExportField {
  context_type: string;
  value_name: string;
}

export interface CreateExportJobRequest {
  device_ids?: string[];
  location_ids?: string[];
  installation_group_oid?: string;
  energy_preset?: EnergyPreset;
  /** Non-energy telemetry channels to include (e.g. heatpump temperatures), keyed by the same
   *  field names as the topology models (topological-telemetry.model.ts). Omitted when none are
   *  selected so existing energy-only exports are unaffected. `null` explicitly requests every
   *  channel (matches the V1 `/telemetry/export/csv` `fields: null` behaviour). */
  fields?: ExportField[] | null;
  granularity: ExportGranularity;
  start_date: string;
  end_date: string;
}

export interface ExportJob {
  export_job_oid: string;
  status: ExportJobStatus;
  device_count: number;
  installation_group_oid: string | null;
  /** Only set when device_count === 1 - the readable identifier (ldn) of that device. */
  device_ldn?: string | null;
  granularity: ExportGranularity;
  /** Energy preset the job was created with. Only populated once the backend persists and
   *  returns it; absent for "all data" exports (which send no preset) and for older jobs. */
  energy_preset?: EnergyPreset | null;
  start_date: string;
  end_date: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  /** True once the file is fetchable via `downloadExportJob` - there is no bare URL; the
   * backend re-checks ownership on every `GET /export/jobs/{oid}/download` call. */
  download_available: boolean;

  // Progress tracking fields (new)
  started_at?: string | null;
  estimated_duration_seconds?: number | null;
  processed_data_points?: number;
  total_data_points?: number;
  processed_devices?: number;
  file_size_mb?: number | null;
  download_url?: string | null;
  retry_count?: number;
}

export interface ExportJobListResponse {
  number_of_items: number;
  jobs: ExportJob[];
}

@Injectable({
  providedIn: 'root',
})
export class InsightsService {
  constructor(private readonly apiService: ApiService) {}

  // A 404 here never means "no data for this period": the route answers 200 with empty intervals
  // for that. It means the device's telemetry is out of the user's reach, which the Insights tab
  // explains itself, so the global snackbar stays out of it.
  getDeviceTopologicalTelemetryByTime(deviceId: string, time: string) {
    return this.apiService
      .getRaw(
        `/telemetry/device/${deviceId}/energyflow/${time}`,
        {},
        new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [404]),
      )
      .pipe(
        map(response => {
          return response;
        }),
      );
  }

  // Read from the device energyflow route until the forecast becomes an installation point. A 404
  // means the device's telemetry is out of the user's reach, and the card simply goes without it.
  getDevicePvForecast(deviceId: string, selected: TelemetryPeriod): Observable<DevicePvForecast> {
    return this.apiService.getRaw<DevicePvForecast>(
      `/telemetry/device/${deviceId}/energyflow/${ENERGYFLOW_SEGMENT[selected.granularity]}/${selected.period}`,
      {},
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [404]),
    );
  }

  /**
   * PV performance ratios for one device, anchored on a single day.
   *
   * The anchor day is path-encoded rather than passed as an ISO string, matching the route's
   * `/{year}/{month}/{day}` shape. It is read in local time on purpose: the day comes from
   * `anchorDayFor`, which resolves the *user's* selected period, so shifting it to UTC would
   * hand the backend a different day than the one on screen.
   *
   * A 404 is the route refusing a device that is not a production meter, or one whose telemetry
   * is not shared with the user; the card has nothing to show either way.
   */
  getDevicePvPerformance(deviceId: string, day: Date): Observable<PvPerformanceResponse> {
    const path = `${day.getFullYear()}/${day.getMonth() + 1}/${day.getDate()}`;
    return this.apiService.getRaw<PvPerformanceResponse>(
      `/telemetry/device/${deviceId}/pv-performance/${path}`,
      {},
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [404]),
    );
  }

  /** The backend only accepts one device per call — see ExportRequest validation. */
  exportDeviceCsv(
    deviceId: string,
    ldn: string,
    startDate: string,
    endDate: string,
    granularity?: ExportGranularity,
  ): Observable<HttpResponse<Blob>> {
    const body = {
      devices: [{ device_id: deviceId, ldn, fields: null }],
      start_date: startDate,
      end_date: endDate,
      ...(granularity ? { granularity } : {}),
    };
    return this.apiService.post<HttpResponse<Blob>>('/telemetry/export/csv', body, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /** Creates a queued export job for one or more devices, or an installation group. */
  createExportJob(request: CreateExportJobRequest): Observable<{ export_job_oid: string }> {
    // The export page renders the failure inline next to the submit button, so the global
    // snackbar would only repeat the same message.
    return this.apiService.post('/telemetry/export/jobs', request, {
      context: new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR, true),
    });
  }

  listExportJobs(
    limit: number,
    offset: number,
    oids?: string[],
  ): Observable<ExportJobListResponse> {
    const params: Record<string, unknown> = { limit, offset };
    if (oids && oids.length > 0) {
      params['oids'] = oids.join(',');
    }
    return this.apiService.get<ExportJobListResponse>('/telemetry/export/jobs', params);
  }

  calculateProgress(job: ExportJob): number {
    if (job.status === 'queued') {
      return 0;
    }
    if (job.status === 'completed') {
      return 100;
    }
    return this.calculateDataProgress(job);
  }

  private calculateDataProgress(job: ExportJob): number {
    if (!job.total_data_points || job.total_data_points === 0) return 0;
    return Math.min(100, ((job.processed_data_points ?? 0) / job.total_data_points) * 100);
  }

  /** Server response is still gzip-compressed (`application/gzip`) - same as `exportDeviceCsv`,
   * the caller decompresses client-side (see `gunzipCsvUtil.gunzipToCsvBlob`). */
  downloadExportJob(exportJobOid: string): Observable<HttpResponse<Blob>> {
    return this.apiService.getBlobResponse(`/telemetry/export/jobs/${exportJobOid}/download`);
  }
}
